import { MRT_ColumnDef, MRT_PaginationState } from "material-react-table";
import { useState } from "react";
import { Box, Button, Chip, IconButton, Typography, Alert } from "@mui/material";
import { Delete, Edit, RemoveRedEye, Send } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { DataTable } from "../../libs/core/components/data-table";
import { ConfirmModal } from "../../libs/ui/modal/confirm_modal";
import { useDeleteNewsletterMutation, useGetNewslettersQuery, useSendNewsletterMutation } from "./api/newsletterApi";
import { Newsletter } from "./types/newsletter";
import AddEditNewsletterDialog from "./components/AddNewsletter";
import DOMPurify from "dompurify";

const NewsLetterManager: React.FC = () => {
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [openSendConfirmModal, setOpenSendConfirmModal] = useState(false);
  const [selectedNewsletter, setSelectedNewsletter] = useState<Newsletter | null>(null);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const navigate = useNavigate();

  const { data: newsletters, isLoading, isError, isFetching } = useGetNewslettersQuery();
  const [deleteNewsletter] = useDeleteNewsletterMutation();
  const [sendNewsletter, { isLoading: isSending }] = useSendNewsletterMutation();

  const columns: Array<MRT_ColumnDef<Newsletter>> = [
    {
      header: "Subject",
      accessorKey: "subject",
      size: 200,
    },


    {
      header: "Content Preview",
      accessorKey: "content",
      size: 300,
      Cell: ({ cell }) => {
        const rawHtml = cell.getValue<string>() || '';

        // Remove HTML tags using a browser-friendly DOM method
        const plainText = DOMPurify.sanitize(rawHtml, { ALLOWED_TAGS: [] })
          .replace(/&nbsp;/g, ' ')
          .trim();

        const preview = plainText.length > 200 ? plainText.substring(0, 200) + '...' : plainText;

        return (
          <Typography
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {preview}
          </Typography>
        );
      },
    },
    {
      header: "Created At",
      accessorKey: "created_at",
      size: 180,
      Cell: ({ cell }) => (
        <Typography>
          {new Date(cell.getValue<string>()).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })}
        </Typography>
      ),
    },

    {
      header: "Actions",
      //   accessorKey: "actions",
      size: 120,
      Cell: ({ row }) => (
        <Box display="flex" gap={1}>
          <IconButton onClick={() => navigate(`/newsletters/${row.original.id}`)}>
            <RemoveRedEye color="primary" />
          </IconButton>
          <IconButton onClick={() => {
            setSelectedNewsletter(row.original);
            setOpenAddDialog(true);
          }}>
            <Edit color="primary" />
          </IconButton>
          <IconButton onClick={() => {
            setSelectedNewsletter(row.original);
            setOpenSendConfirmModal(true);
          }}>
            <Send color="success" />
          </IconButton>
          <IconButton onClick={() => {
            setSelectedNewsletter(row.original);
            setOpenConfirmModal(true);
          }}>
            <Delete color="error" />
          </IconButton>
        </Box>
      ),
    },
  ];

  const handleDelete = () => {
    if (selectedNewsletter) {
      console.log('Deleting newsletter:', selectedNewsletter.id);
      deleteNewsletter(selectedNewsletter.id.toString())
      setOpenConfirmModal(false);

    }
  };

  const handleSend = async () => {
    if (selectedNewsletter) {
      try {
        setSendError(null);
        await sendNewsletter({ 
          id: selectedNewsletter.id.toString(),
          body: {} // Empty body since we're just sending the newsletter
        }).unwrap();
        // Only close dialog and clear selection on success
        setOpenSendConfirmModal(false);
        setSelectedNewsletter(null);
      } catch (error: any) {
        console.error('Failed to send newsletter:', error);
        setSendError(error?.data?.message || 'Failed to send newsletter. Please try again.');
        // Dialog stays open on error so user can see the error message and try again
      }
    }
  };

  return (
    <Box>
      <DataTable<Newsletter>
        toolbar={
          <Box display="flex" alignItems="center" width="100%">
            <Box display="flex" gap={3} alignItems="center" flex="1">
              <Typography variant="h6">Newsletters</Typography>
              <Chip
                color="primary"
                variant="outlined"
                label={`${newsletters?.data?.length ?? 0} Newsletters`}
              />
            </Box>

            <Box display="flex" gap={2}>
              <Button
                onClick={() => {
                  setSelectedNewsletter(null);
                  setOpenAddDialog(true);
                }}
                variant="contained"
              >
                Create Newsletter
              </Button>
            </Box>
          </Box>
        }

        list={newsletters?.data ?? []}
        count={newsletters?.data?.length ?? 0}
        columns={columns}
        getRowId={(row) => row?.id?.toString()}
        isLoading={isLoading}
        isSuccess={!isError}
        isError={isError}
        isFetching={isFetching}
        pagination={pagination}
        onPaginationChange={setPagination}
      // onRowClick={(row) => navigate(`/newsletters/${row.id}`)}
      />

      {selectedNewsletter && (
        <>
          <ConfirmModal
            type="warning"
            title="Delete Newsletter"
            message={`Are you sure you want to delete '${selectedNewsletter.subject}'?`}
            modalOpen={openConfirmModal}
            setModalOpen={setOpenConfirmModal}
            onOk={() => {
              handleDelete();
              setOpenConfirmModal(false);
              setSelectedNewsletter(null);
            }}
          />

          <ConfirmModal
            type="success"
            title="Send Newsletter"
            message={
              <>
             
                <Typography>
                  Are you sure you want to send '{selectedNewsletter.subject}' to all subscribers?
                </Typography>
                   {sendError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {sendError}
                  </Alert>
                )}
              </>
            }
            modalOpen={openSendConfirmModal}
            setModalOpen={setOpenSendConfirmModal}
            onOk={handleSend}
            loading={isSending}
            customOkText="Yes, Send"
            customCancelText="No"
            hideCancelButton={false}
          />
        </>
      )}

      <AddEditNewsletterDialog 
      newsletterToEdit={selectedNewsletter}
      open={openAddDialog} setOpen={setOpenAddDialog} />

    </Box>
  );
};

export default NewsLetterManager;


